import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Film, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1627244714766-94dab62ed964?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "editor" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success("Bem-vindo à sala de edição");
      navigate("/dashboard");
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex bg-[#08080C]">
      <div className="grain" />
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Estúdio de edição" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 p-14 flex flex-col justify-end z-10">
          <div className="w-12 h-12 rounded-xl bg-[#FF2E93] flex items-center justify-center mb-6">
            <Film className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-head font-700 text-4xl lg:text-5xl text-white leading-tight">
            Da gravação bruta<br />ao <span className="text-[#FF2E93]">corte pronto</span>.
          </h1>
          <p className="mt-4 text-[#A1A1AA] max-w-md text-base">
            Transcrição em PT-BR, roteiro por IA no estilo do reality e mapa de edição pronto pro editor.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 relative z-10">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-9 h-9 rounded-lg bg-[#FF2E93] flex items-center justify-center">
              <Film className="w-5 h-5 text-white" />
            </div>
            <span className="font-head font-700 text-lg">RealityEditor</span>
          </div>

          <h2 className="font-head font-700 text-3xl mb-1">
            {mode === "login" ? "Entrar no estúdio" : "Criar conta"}
          </h2>
          <p className="text-[#A1A1AA] text-sm mb-8">
            {mode === "login" ? "Acesse seus projetos e mapas de edição." : "Junte-se à equipe de produção."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Nome</label>
                <input
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={set("name")}
                  required
                  className="mt-1 w-full bg-[#121218] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200"
                  placeholder="Seu nome"
                />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A1A1AA]">E-mail</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                className="mt-1 w-full bg-[#121218] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200"
                placeholder="voce@estudio.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Senha</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                className="mt-1 w-full bg-[#121218] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors duration-200"
                placeholder="••••••••"
              />
            </div>
            {mode === "register" && (
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Papel</label>
                <select
                  data-testid="register-role-select"
                  value={form.role}
                  onChange={set("role")}
                  className="mt-1 w-full bg-[#121218] border border-[#282834] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF]"
                >
                  <option value="editor">Editor / Equipe</option>
                  <option value="producer">Produtor / Admin</option>
                </select>
              </div>
            )}

            {error && <p className="text-sm text-[#FF2E93]" data-testid="auth-error">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full bg-[#FF2E93] hover:bg-[#ff54a6] text-white font-semibold rounded-full py-3.5 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Entrar" : "Criar conta"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#A1A1AA]">
            {mode === "login" ? "Ainda não tem conta?" : "Já tem conta?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-[#00E5FF] hover:underline"
            >
              {mode === "login" ? "Cadastre-se" : "Entrar"}
            </button>
          </p>

          {mode === "login" && (
            <div className="mt-8 p-4 rounded-lg bg-[#121218] border border-[#282834]">
              <p className="text-xs text-[#A1A1AA] mb-1">Contas de demonstração:</p>
              <p className="text-xs text-[#F4F4F5] font-mono">produtor@realityeditor.com / produtor123</p>
              <p className="text-xs text-[#F4F4F5] font-mono">editor@realityeditor.com / editor123</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
